import { queryOptions, infiniteQueryOptions } from '@tanstack/react-query';
import type { ApiClient } from './client.ts';
import { unwrap } from './client.ts';
import { qk } from './keys.ts';
import type { components, operations } from './schema.d.ts';

export type Schemas = components['schemas'];
export type User = Schemas['User'];
export type Settings = Schemas['Settings'];
export type City = Schemas['City'];
export type Station = Schemas['Station'];
export type PlaceCard = Schemas['PlaceCard'];
export type PlaceDetail = Schemas['PlaceDetail'];
export type PlaceInsights = Schemas['Insights'];
export type HotelInfo = Schemas['HotelInfo'];
export type Category = Schemas['Category'];
export type SearchResult = Schemas['OutputBody'];
export type Trip = Schemas['Trip'];
export type TripSummary = Schemas['Summary'];
export type TripItem = Schemas['Item'];
export type TripDay = Schemas['Day'];
export type Breakdown = Schemas['Breakdown'];
export type ServiceView = Schemas['ServiceView'];
export type Forecast = Schemas['Forecast'];
export type TravelStats = Schemas['TravelStats'];
export type SegmentView = Schemas['SegmentView'];
export type TimelineEntry = Schemas['TimelineEntry'];
export type RouteView = Schemas['RouteView'];
export type Position = Schemas['Position'];
export type Carrier = Schemas['Carrier'];
export type SnapshotMsg = Schemas['SnapshotMsg'];
export type PositionMsg = Schemas['PositionMsg'];
export type StatusMsg = Schemas['StatusMsg'];
export type ServiceStatus = ServiceView['status'];
export type ServiceKind = ServiceView['kind'];
export type Review = Schemas['Review'];
export type Reply = Schemas['Reply'];
export type RatingSummary = Schemas['RatingSummary'];
export type MediaRef = Schemas['Ref'];
export type Notification = Schemas['Notification'];
export type NotificationPrefs = Schemas['Prefs'];
export type PushSubscriptionView = Schemas['SubscriptionView'];
export type ReviewsQuery = NonNullable<operations['reviews.list']['parameters']['query']>;
export type CreateSegmentBody = NonNullable<operations['tracking.createSegment']['requestBody']>['content']['application/json'];
export type ServicesSearchQuery = NonNullable<operations['tracking.searchServices']['parameters']['query']>;
/** SSE event map for /segments/{id}/stream and /me/stream. */
export type TrackingEvents = { snapshot: SnapshotMsg; position: PositionMsg; status: StatusMsg };
export type PlacesQuery = NonNullable<operations['catalog.listPlaces']['parameters']['query']>;
export type HotelsQuery = NonNullable<operations['catalog.listHotels']['parameters']['query']>;

/** Query option factories bound to an API client. Use with useQuery / ensureQueryData. */
export function createQueries(api: ApiClient) {
  return {
    me: () =>
      queryOptions({
        queryKey: qk.me(),
        queryFn: async () => {
          const res = await api.GET('/me');
          if (res.response.status === 401) return null;
          return unwrap(res);
        },
        staleTime: 60_000,
      }),
    cities: (q?: string, limit = 20) =>
      queryOptions({
        queryKey: qk.cities(q),
        queryFn: async () => unwrap(await api.GET('/cities', { params: { query: { q: q || undefined, limit } } })).items,
        staleTime: 10 * 60_000,
      }),
    city: (slug: string) =>
      queryOptions({
        queryKey: qk.city(slug),
        queryFn: async () => unwrap(await api.GET('/cities/{slug}', { params: { path: { slug } } })),
        staleTime: 10 * 60_000,
      }),
    places: (params: PlacesQuery) =>
      queryOptions({
        queryKey: qk.places(params as Record<string, unknown>),
        queryFn: async () => unwrap(await api.GET('/places', { params: { query: params } })),
        staleTime: 5 * 60_000,
      }),
    placesInfinite: (params: Omit<PlacesQuery, 'cursor'>) =>
      infiniteQueryOptions({
        queryKey: [...qk.places(params as Record<string, unknown>), 'inf'] as const,
        queryFn: async ({ pageParam }) => unwrap(await api.GET('/places', { params: { query: { ...params, cursor: pageParam || undefined } } })),
        initialPageParam: '',
        getNextPageParam: (last) => last.next_cursor ?? undefined,
        staleTime: 5 * 60_000,
      }),
    hotels: (params: HotelsQuery) =>
      queryOptions({
        queryKey: qk.hotels(params as Record<string, unknown>),
        queryFn: async () => unwrap(await api.GET('/hotels', { params: { query: params } })),
        staleTime: 5 * 60_000,
      }),
    place: (idOrSlug: string) =>
      queryOptions({
        queryKey: qk.place(idOrSlug),
        queryFn: async () => unwrap(await api.GET('/places/{idOrSlug}', { params: { path: { idOrSlug } } })),
        staleTime: 5 * 60_000,
      }),
    placeInsights: (idOrSlug: string, lang: 'fa' | 'en') =>
      queryOptions({
        queryKey: qk.placeInsights(idOrSlug, lang),
        queryFn: async () => unwrap(await api.GET('/places/{idOrSlug}/insights', { params: { path: { idOrSlug }, query: { lang } } })),
        staleTime: 60 * 60_000,
      }),
    categories: () =>
      queryOptions({
        queryKey: ['categories'] as const,
        queryFn: async () => unwrap(await api.GET('/categories')).items,
        staleTime: 60 * 60_000,
      }),
    favorites: () =>
      queryOptions({
        queryKey: qk.favorites(),
        queryFn: async () => unwrap(await api.GET('/me/favorites')).items,
      }),
    search: (q: string, params?: { city?: string; types?: string; limit?: number }) =>
      queryOptions({
        queryKey: qk.search(q, params),
        queryFn: async () => unwrap(await api.GET('/search', { params: { query: { q, ...params } } })),
        enabled: q.trim().length >= 2,
        staleTime: 60_000,
      }),
    trips: (scope: 'upcoming' | 'past' | 'all' = 'all') =>
      queryOptions({
        queryKey: qk.trips(scope),
        queryFn: async () => unwrap(await api.GET('/trips', { params: { query: { scope } } })).items,
        staleTime: 30_000,
      }),
    trip: (id: string) =>
      queryOptions({
        queryKey: qk.trip(id),
        queryFn: async () => unwrap(await api.GET('/trips/{id}', { params: { path: { id } } })),
        staleTime: 30_000,
      }),
    tripBudget: (id: string) =>
      queryOptions({
        queryKey: qk.tripBudget(id),
        queryFn: async () => unwrap(await api.GET('/trips/{id}/budget', { params: { path: { id } } })),
      }),
    sharedTrip: (token: string) =>
      queryOptions({
        queryKey: ['shared', token] as const,
        queryFn: async () => unwrap(await api.GET('/shared/{token}', { params: { path: { token } } })),
      }),
    // ---- tracking ----
    carriers: (kind?: ServiceKind) =>
      queryOptions({
        queryKey: ['carriers', kind ?? ''] as const,
        queryFn: async () => unwrap(await api.GET('/carriers', { params: { query: { kind: kind ?? '' } } })).items,
        staleTime: 60 * 60_000,
      }),
    servicesSearch: (params: ServicesSearchQuery) =>
      queryOptions({
        queryKey: qk.services(params as Record<string, unknown>),
        queryFn: async () => unwrap(await api.GET('/services/search', { params: { query: params } })).items,
        staleTime: 60_000,
      }),
    serviceLookup: (number: string, date: string) =>
      queryOptions({
        queryKey: qk.services({ number, date }),
        queryFn: async () => unwrap(await api.GET('/services/lookup', { params: { query: { number, date } } })).items,
        enabled: number.trim().length >= 2 && !!date,
        staleTime: 60_000,
      }),
    service: (id: string) =>
      queryOptions({
        queryKey: ['services', 'detail', id] as const,
        queryFn: async () => unwrap(await api.GET('/services/{id}', { params: { path: { id } } })),
        staleTime: 15_000,
      }),
    segments: (scope: 'upcoming' | 'past' | 'all' = 'upcoming') =>
      queryOptions({
        queryKey: qk.segments(scope),
        queryFn: async () => unwrap(await api.GET('/segments', { params: { query: { scope } } })).items,
        staleTime: 15_000,
      }),
    segment: (id: string) =>
      queryOptions({
        queryKey: qk.segment(id),
        queryFn: async () => unwrap(await api.GET('/segments/{id}', { params: { path: { id } } })),
        staleTime: 15_000,
      }),
    segmentTimeline: (id: string) =>
      queryOptions({
        queryKey: qk.segmentTimeline(id),
        queryFn: async () => unwrap(await api.GET('/segments/{id}/timeline', { params: { path: { id } } })).items,
        staleTime: 15_000,
      }),
    segmentForecast: (id: string) =>
      queryOptions({
        queryKey: qk.segmentForecast(id),
        queryFn: async () => unwrap(await api.GET('/segments/{id}/forecast', { params: { path: { id } } })),
        staleTime: 10 * 60_000,
      }),
    travelStats: () =>
      queryOptions({
        queryKey: qk.travelStats(),
        queryFn: async () => unwrap(await api.GET('/me/travel-stats')),
        staleTime: 60_000,
      }),
    segmentRoute: (id: string) =>
      queryOptions({
        queryKey: qk.segmentRoute(id),
        queryFn: async () => unwrap(await api.GET('/segments/{id}/route', { params: { path: { id } } })),
        staleTime: 10 * 60_000,
      }),
    segmentPositions: (id: string, since?: string) =>
      queryOptions({
        queryKey: [...qk.segment(id), 'positions', since ?? ''] as const,
        queryFn: async () => unwrap(await api.GET('/segments/{id}/positions', { params: { path: { id }, query: { since } } })).items,
        staleTime: 30_000,
      }),
    tripSegments: (tripId: string) =>
      queryOptions({
        queryKey: [...qk.trip(tripId), 'segments'] as const,
        queryFn: async () => unwrap(await api.GET('/trips/{tripId}/segments', { params: { path: { tripId } } })).items,
        staleTime: 15_000,
      }),
    // ---- reviews ----
    reviewsInfinite: (placeId: string, params?: Omit<ReviewsQuery, 'cursor'>) =>
      infiniteQueryOptions({
        queryKey: [...qk.reviews(placeId, params as Record<string, unknown>), 'inf'] as const,
        queryFn: async ({ pageParam }) => unwrap(await api.GET('/places/{id}/reviews', { params: { path: { id: placeId }, query: { ...params, cursor: pageParam || undefined } } })),
        initialPageParam: '',
        getNextPageParam: (last) => last.next_cursor ?? undefined,
        staleTime: 30_000,
      }),
    review: (id: string) =>
      queryOptions({
        queryKey: ['reviews', 'detail', id] as const,
        queryFn: async () => unwrap(await api.GET('/reviews/{id}', { params: { path: { id } } })),
      }),
    reviewReplies: (id: string) =>
      queryOptions({
        queryKey: ['reviews', 'detail', id, 'replies'] as const,
        queryFn: async () => unwrap(await api.GET('/reviews/{id}/replies', { params: { path: { id } } })).items,
      }),
    myReviews: () =>
      queryOptions({
        queryKey: ['me', 'reviews'] as const,
        queryFn: async () => unwrap(await api.GET('/me/reviews')).items,
        staleTime: 30_000,
      }),
    // ---- notifications ----
    notificationsInfinite: () =>
      infiniteQueryOptions({
        queryKey: [...qk.notifications(), 'inf'] as const,
        queryFn: async ({ pageParam }) => unwrap(await api.GET('/notifications', { params: { query: { cursor: pageParam || undefined, limit: 30 } } })),
        initialPageParam: '',
        getNextPageParam: (last) => last.next_cursor ?? undefined,
        staleTime: 15_000,
      }),
    unreadCount: () =>
      queryOptions({
        queryKey: qk.unread(),
        queryFn: async () => unwrap(await api.GET('/notifications/unread-count')).unread,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      }),
    notificationPrefs: () =>
      queryOptions({
        queryKey: ['me', 'notification-prefs'] as const,
        queryFn: async () => unwrap(await api.GET('/me/notification-prefs')),
      }),
    vapidPublicKey: () =>
      queryOptions({
        queryKey: ['push', 'vapid'] as const,
        queryFn: async () => unwrap(await api.GET('/push/vapid-public-key')).key,
        staleTime: Infinity,
      }),
    pushSubscriptions: () =>
      queryOptions({
        queryKey: ['push', 'subscriptions'] as const,
        queryFn: async () => unwrap(await api.GET('/push/subscriptions')).items,
      }),
    stations: (params?: { city?: string; kind?: 'airport' | 'rail' | 'bus_terminal' | ''; q?: string; limit?: number }) =>
      queryOptions({
        queryKey: ['stations', params ?? {}] as const,
        queryFn: async () => unwrap(await api.GET('/stations', { params: { query: params } })).items,
        staleTime: 60 * 60_000,
      }),
  };
}
export type Queries = ReturnType<typeof createQueries>;
